// A report variant's two groups, confirmed on its tile in "Variants" (TGCLOUD-1868, plan B1, D3;
// decision 15):
//
//   Variant                     its identity: the c., p. and g. descriptions, the transcript, the
//                               genome build and the zygosity
//   Laboratory classification   the interpretation the Test records, with the lab's own wording
//                               riding on it
//
// A value of either the reader was not confident of arrives empty and is entered here
// (LowConfidenceField), and each card has its own Confirm (GroupCard).
//
// DECISION 15. The lab's classification as printed is saved verbatim whatever interpretation the
// clinician chooses beside it. While the interpretation is one the reader was not confident of,
// the card says so, "Saved with the laboratory's own wording, as printed.", and points to where
// the report prints it.
//
// FindingsNotOnTest: a finding of the report that no variant on the Test carries (the Test kept
// its own variant, the clinician removed it, or a contradiction is still to be chosen). Nothing of
// it is saved, and its two groups are still confirmed, as the save requires: each says so.
import React from 'react';
import { Box, Typography } from '@mui/material';
import { variantLabel } from 'src/components/pedigree-chart/pedigree-sidebar/sections/test-results/applyProposal';
import {
  GENOME_BUILDS,
  TRANSCRIPT_HELP,
  ZYGOSITIES,
  ZYGOSITY_LABELS,
  isVersionedTranscript
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/test-record/testRecordModel';
import AnchorMark from './AnchorMark';
import GroupCard from './GroupCard';
import LowConfidenceField from './LowConfidenceField';
import { useReadingChecksContext } from './useReadingChecks';
import { variantTarget } from './verificationTargets';
import {
  CONFIRM_LABELS,
  GROUP_KIND_TEXT,
  LAB_WORDING_KEPT,
  NOT_ON_TEST_CONFIRM,
  NOT_ON_TEST_HEADING,
  NOT_ON_TEST_KEPT,
  NOT_ON_TEST_REMOVED
} from './labReportVerificationVocabulary';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const getPath = (object, path) =>
  String(path || '')
    .split('.')
    .reduce((v, key) => (v == null ? undefined : v[key]), object);
const hasText = (v) => typeof v === 'string' && v.trim() !== '';

const BUILD_OPTIONS = GENOME_BUILDS.map((b) => ({ value: b, label: b }));
// The zygosities in words, in the selected language: LowConfidenceField shows an option's label as
// it is, since another list's labels may be a site's own values.
const zygosityOptions = (t) =>
  ZYGOSITIES.map((z) => ({ value: z, label: t(ZYGOSITY_LABELS[z]) }));

// The low-confidence values of `group` that are about this variant.
const lowOfVariant = (checks, group, findingId) =>
  group
    ? checks
        .lowOf(group.key)
        .filter(
          (l) => l.place?.kind === 'variant' && l.place.findingId === findingId
        )
    : [];

/**
 * @param {object} props
 * @param {object} props.variant   the Test's variant that is the report's finding
 * @param {(findingId: string, field: string, value) => void} props.onSetField
 * @param {Array<{ value, label }>} props.interpretationOptions  the form's interpretation list
 *   (VARIANT_INTERPRETATION_OPTIONS): fixed English labels, translated here
 */
export default function VariantGroupCards({
  variant,
  onSetField,
  interpretationOptions = []
}) {
  const t = useAutoTranslation();

  const checks = useReadingChecksContext();
  if (!checks || !checks.active || !variant || !hasText(variant.findingId)) {
    return null;
  }
  const { findingId } = variant;
  const identity = checks.identityGroupOf(findingId);
  const classification = checks.classificationGroupOf(findingId);
  if (!identity && !classification) return null;
  const name = variantLabel(variant);
  const target = variantTarget(findingId);
  const optionsOf = (field) =>
    field === 'genomeBuild'
      ? BUILD_OPTIONS
      : field === 'zygosity'
      ? zygosityOptions(t)
      : field === 'variantInterpretation'
      ? interpretationOptions.map((o) => ({ ...o, label: t(o.label) }))
      : [];
  const field = (low) => {
    const at = low.place.field;
    const value = getPath(variant, at);
    const transcript = at === 'transcript';
    return (
      <LowConfidenceField
        key={low.path}
        path={low.path}
        field={at}
        value={value}
        onChange={(next) => onSetField(findingId, at, next)}
        target={target}
        name={name}
        options={optionsOf(at)}
        helperText={transcript ? t(TRANSCRIPT_HELP) : null}
        error={
          transcript && hasText(value) && !isVersionedTranscript(value.trim())
        }
      />
    );
  };
  const interpretationLow = lowOfVariant(
    checks,
    classification,
    findingId
  ).find((l) => l.place.field === 'variantInterpretation');
  // The lab's wording is saved whenever the report prints it: read (and withheld here with the
  // value), or kept on the variant where it names no tier of the five.
  const wordingKept =
    !!interpretationLow &&
    (!!interpretationLow.readingDigest ||
      hasText(variant.labClassification?.asPrinted));

  return (
    <Box data-testid="variant-group-cards" data-finding={findingId}>
      {identity && (
        <GroupCard
          groupKey={identity.key}
          title={t(GROUP_KIND_TEXT['variant-identity'])}
          confirmLabel={t(CONFIRM_LABELS['variant-identity'])}
          name={name}
        >
          {lowOfVariant(checks, identity, findingId).map(field)}
        </GroupCard>
      )}
      {classification && (
        <GroupCard
          groupKey={classification.key}
          title={t(GROUP_KIND_TEXT['variant-classification'])}
          confirmLabel={t(CONFIRM_LABELS['variant-classification'])}
          name={name}
        >
          {wordingKept && (
            <Box data-testid="lab-wording-kept">
              <Typography variant="body2" component="span">
                {t(LAB_WORDING_KEPT)}
              </Typography>
              <AnchorMark
                target={target}
                fields={['labClassification', 'variantInterpretation']}
                label={t('{{value}} of {{name}}', {
                  value: t(GROUP_KIND_TEXT['variant-classification']),
                  name
                })}
              />
            </Box>
          )}
          {lowOfVariant(checks, classification, findingId).map(field)}
        </GroupCard>
      )}
    </Box>
  );
}

/** The reading's findings no variant on the Test carries, each with its two groups to confirm. */
export function FindingsNotOnTest() {
  const t = useAutoTranslation();

  const checks = useReadingChecksContext();
  if (!checks || !checks.active || !checks.notOnForm.length) return null;
  const plan = checks.review?.application?.plan;
  const choices = checks.review?.choices || {};
  const kept = (findingId) =>
    (plan?.variants || []).some(
      (v) =>
        v.findingId === findingId &&
        v.kind === 'conflict' &&
        choices[v.id] === 'current'
    );
  return (
    <Box data-testid="findings-not-on-test" sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {t(NOT_ON_TEST_HEADING)}
      </Typography>
      {checks.notOnForm.map(({ findingId, variant, groups }) => {
        const open = checks.openConflicts.some(
          (c) => c.findingId === findingId
        );
        return (
          <Box
            key={findingId}
            data-testid="finding-not-on-test"
            data-finding={findingId}
            sx={{
              mt: 1,
              p: 1,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {variantLabel(variant) || t(GROUP_KIND_TEXT['variant-identity'])}
            </Typography>
            {!open && (
              <Typography
                variant="caption"
                component="div"
                sx={{ color: 'text.secondary' }}
              >
                {t(kept(findingId) ? NOT_ON_TEST_KEPT : NOT_ON_TEST_REMOVED)}{' '}
                {t(NOT_ON_TEST_CONFIRM)}
              </Typography>
            )}
            {groups.map((g) => (
              <GroupCard
                key={g.key}
                groupKey={g.key}
                title={t(GROUP_KIND_TEXT[g.kind])}
                confirmLabel={t(CONFIRM_LABELS[g.kind])}
                name={variantLabel(variant) || null}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
